import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"
import React, { useEffect, useMemo, useState } from "react"
import dayjs from "dayjs"
import PriceService from "src/services/price-service"
import PropTypes from "prop-types"
import { dispatchSnackbar } from "../../../../components/SnackBar/snackbar-bus"
import { columnsPriceTable } from "./tabela-preco-constants"
import GraficoDateRangeModal from "./Grafico/grafico-date-range-modal"
import DataGridTable from "../../../../components/Table/data-grid-table"

const TabelaPreco = ({ date, loading, setLoading, setExportFileCsv }) => {
    const priceService = useMemo(() => new PriceService(), [])
    const [priceList, setPriceList] = useState([])
    const [isChartOpen, setIsChartOpen] = useState(false)
    const [selectedRowData, setSelectedRowData] = useState(null)
    const [chart, setChart] = useState({
        title: "Gráfico de PU",
        xTitle: "Data Referência",
        yTitle: "PU",
        yRightTitle: "%PUPAR",
        data: []
    })
    const [csvData, setCsvData] = useState([])

    useEffect(() => {
        if (!date || !dayjs(date).isValid()) return

        let ignoreResponse = false

        setExportFileCsv(() => async () => {
            return priceService.exportDataToCsv(date)
        })

        const loadPrices = async () => {
            setLoading(true)
            try {
                const response = await priceService.getPricesv2(date, date)
                const { body } = response
                const { data } = body
                const rows = data.map((obj, index) => ({ ...obj, id: index }))

                if (ignoreResponse) return

                setPriceList(rows)
            } catch {
                if (ignoreResponse) return

                setPriceList([])
                dispatchSnackbar("Não foi possível carregar os preços de PU/TIR.", "error")
            } finally {
                if (!ignoreResponse) setLoading(false)
            }
        }

        loadPrices()

        return () => {
            ignoreResponse = true
        }
    }, [date, priceService, setExportFileCsv, setLoading])

    const handleOpenChart = (rowData) => {
        setSelectedRowData(rowData)
        setIsChartOpen(true)
    }

    const generateCsvData = (body) => {
        const { PU_MTM, PUPAR, PercentualPUPAR } = body

        const data = PU_MTM.map((item, index) => ({
            DataReferencia: item.DataReferencia,
            CETIP: item.CETIP,
            PU_MTM: formatStringToFloatDecimalCase(item.PU_MTM),
            PUPAR: formatStringToFloatDecimalCase(PUPAR[index].PUPAR),
            PercentualPUPAR: formatStringToFloatDecimalCase(PercentualPUPAR[index].PercentualPUPAR),
        }))

        setCsvData(data)
    }

    const loadChartData = (date) => {
        if (!selectedRowData) return

        setLoading(true)
        const cetipList = [selectedRowData.CETIP]

        priceService.generatePriceData(date.firstDate, date.endDate, cetipList)
            .then((response) => {
                const { body } = response
                generateCsvData(body)

                const chartData = priceService.generatePUPARChart(body)

                setChart(prev => ({
                    ...prev,
                    data: chartData
                }))
            })
            .catch(() => {
                setIsChartOpen(false)
                dispatchSnackbar("Não foi possível carregar os dados, tente novamente", "warning")
            })
            .finally(() => setLoading(false))
    }

    return (
        <>
            <DataGridTable
                rows={priceList}
                columns={columnsPriceTable(date, handleOpenChart)}
                loading={loading}
                rowPadding={2}
                contained
                autoRowHeight
                pointerCursor
                disableRowSelectionOnClick
                sortingOrder={['asc', 'desc']}
            />

            <GraficoDateRangeModal
                open={isChartOpen}
                onClose={() => setIsChartOpen(false)}
                title={`Gráfico de PUPAR - ${selectedRowData?.CETIP || ''}`}
                loadChartData={loadChartData}
                loading={loading}
                chartData={chart}
                csvData={csvData}
            />
        </>
    )
}

TabelaPreco.propTypes = {
    date: PropTypes.string,
    setLoading: PropTypes.func,
    loading: PropTypes.bool.isRequired,
    setExportFileCsv: PropTypes.func,
}

export default TabelaPreco
